import { BreadcrumbItem } from 'ish-core/models/breadcrumb-item/breadcrumb-item.interface';
import { CategoryTree } from 'ish-core/models/category-tree/category-tree.model';
import { CategoryView, createCategoryView } from 'ish-core/models/category-view/category-view.model';
import { generateCategoryUrl } from 'ish-core/routing/category/category.route';

/** Top-level shopping departments under the Shop catalog root (excludes My Warehouse). */
export const PRESIDIO_SHOP_DEPARTMENT_IDS = ['HW', 'AV', 'OFFICE', 'NETCOM', 'MOBILE', 'CLOUDSEC'] as const;

const SHOP_ROOT_ID = 'SHOP';

function departmentRootId(category: CategoryView | undefined | null): string {
  if (!category) {
    return '';
  }
  const fromPath = (category.categoryPath?.[0] || '').trim();
  const fromId = (category.uniqueId || '').trim();
  const candidate = fromPath || fromId;
  return candidate.split('.')[0].toUpperCase();
}

/** True when the category sits under a Shop department (not Shop itself, not Warehouse). */
export function isPresidioShopCatalogCategory(category: CategoryView | undefined | null): boolean {
  const root = departmentRootId(category);
  if (!root || root === SHOP_ROOT_ID || root === 'WH') {
    return false;
  }
  return (PRESIDIO_SHOP_DEPARTMENT_IDS as readonly string[]).includes(root);
}

/**
 * Prepends Shop to category/product breadcrumbs so department and PLP trails read
 * Home / Shop / … even though department uniqueIds stay peer roots (`HW`, not `SHOP.HW`).
 */
export function prependPresidioShopBreadcrumb(
  items: BreadcrumbItem[] | undefined,
  category: CategoryView | undefined | null,
  tree: CategoryTree
): BreadcrumbItem[] | undefined {
  if (!items?.length || !isPresidioShopCatalogCategory(category)) {
    return items;
  }

  const shop = createCategoryView(tree, SHOP_ROOT_ID);
  // SHOP may not be in the tree yet (it is not a top-level nav root); still link to it.
  const shopLink = shop?.uniqueId ? generateCategoryUrl(shop) : '/c/SHOP';
  if (items.some(item => item.link === shopLink || (item.text || '').toLowerCase() === 'shop')) {
    return items;
  }

  return [{ text: shop?.name || 'Shop', link: shopLink }, ...items];
}
